/*
 * Backbone Craft Co — Wishlist
 * Multiple named lists per customer (a primary "Wishlist" that can't be
 * deleted, plus any number of custom lists), stored in the customer
 * metafield custom.wishlist for logged-in customers, localStorage for
 * guests. See backbone-wishlist-worker for the save path -- it owns the
 * real schema validation; this file mirrors those same rules client-side
 * for a responsive UI, not as the source of truth.
 */

const BackboneWishlist = (() => {

  // ─── CONFIG ────────────────────────────────────────────────────────────────
  const STORAGE_KEY = 'backbone_wishlist';
  const API_VERSION = '2026-07';
  const PRIMARY_LIST_ID = 'wishlist';
  const PRIMARY_LIST_NAME = 'Wishlist';
  // Still needed for the Storefront API product lookups in
  // renderWishlistPage() -- reading product data is a normal Storefront
  // API operation, unlike writing a customer metafield (see saveToMetafield).
  const STOREFRONT_TOKEN = document.querySelector('meta[name="shopify-storefront-token"]')?.content;
  const SHOP_DOMAIN = document.querySelector('meta[name="shopify-domain"]')?.content || window.Shopify?.shop;
  const CUSTOMER_ID = document.querySelector('meta[name="customer-id"]')?.content;
  const LOGIN_URL = document.querySelector('meta[name="wishlist-login-url"]')?.content || '/account/login';
  const ALL_PRODUCTS_URL = document.querySelector('meta[name="wishlist-all-products-url"]')?.content || '/collections/all';
  // customer.access_token renders as an empty string under this store's
  // Customer Accounts system (confirmed live: the meta tag is present but
  // content="" while genuinely logged in), so a token-based login check was
  // always false -- every "logged in" session was silently falling through
  // to the guest/localStorage path instead of ever attempting a metafield
  // save. customer.id doesn't have this problem, so use that instead.
  const IS_LOGGED_IN = !!CUSTOMER_ID;

  // ─── STATE ─────────────────────────────────────────────────────────────────
  // lists: [{ id, name, primary, items: [{ productId, variantId }] }]
  let lists = [];
  // Which list the wishlist page is currently showing. Defaults to the
  // primary list once init() runs.
  let activeListId = PRIMARY_LIST_ID;
  // Set while the wishlist page is showing the "this list has items --
  // move them or delete anyway?" panel for a given list id.
  let pendingDeleteListId = null;
  // Purchasable variants for whatever list is currently rendered, set by
  // renderListItems -- lets "Add all to cart" skip sold-out items without
  // a second Storefront round trip to re-check availability.
  let activeListAddableItems = [];
  // The variant currently selected on a product page, kept in sync via
  // the theme's own variant-change pub/sub event (see bindVariantChangeListener).
  // null everywhere except a product page that actually has a variant
  // picker -- a single-variant product has no picker and no real choice
  // to record, so it's left null and behaves exactly like pre-#9 data.
  let currentProductVariantId = null;
  // Global "notify me about restocks/price-drops" preference (issue #11)
  // -- null until fetched (logged-in only; guests have no Klaviyo profile
  // to key this off), then true/false once known.
  let notifyPreferenceEnabled = null;

  // ─── INIT ──────────────────────────────────────────────────────────────────
  async function init() {
    // A shared-list link (see buildShareUrl) always lands on this same
    // page -- reusing it rather than a separate template means no extra
    // Admin setup, and it already has everything needed: the container,
    // the Storefront product-lookup code, the styling. This is a
    // completely separate, read-only rendering path -- it never touches
    // the visiting browser's own wishlist state (guest or logged in).
    const shareParams = getShareParamsFromUrl();
    if (shareParams && getWishlistPageContainer()) {
      await renderSharedListView(shareParams);
      return;
    }

    if (IS_LOGGED_IN) {
      lists = loadFromMetafield();
      await mergeGuestWishlist();
    } else {
      lists = loadFromLocalStorage();
    }

    activeListId = PRIMARY_LIST_ID;
    currentProductVariantId = getSelectedVariantId();

    renderHearts();
    renderListPickers();
    updateBadge();
    bindEvents();
    bindVariantChangeListener();

    if (getWishlistPageContainer()) {
      if (IS_LOGGED_IN) {
        notifyPreferenceEnabled = await fetchNotifyPreference();
      }
      renderWishlistPage();
    }
  }

  // Only ever called from the wishlist page (not fetched sitewide) --
  // there's nowhere else this preference is shown or needed.
  async function fetchNotifyPreference() {
    try {
      const response = await fetch('/apps/wishlist/notify-preference');
      const result = await response.json();
      return result.success ? result.enabled : false;
    } catch (err) {
      console.error('Backbone Wishlist: Failed to load notify preference', err);
      return false;
    }
  }

  // ─── NORMALIZE / MIGRATE ───────────────────────────────────────────────────
  // Wraps a pre-Wishlist-2.0 flat array of product id strings into a single
  // primary list. Both the metafield (until a customer's next save) and
  // localStorage may still hold this old shape.
  function isLegacyFlatArray(data) {
    return Array.isArray(data) && data.every(entry => typeof entry === 'string');
  }

  function normalizeItem(item) {
    if (typeof item === 'string') return { productId: item, variantId: null };
    return { productId: String(item.productId), variantId: item.variantId ?? null };
  }

  function normalizeLists(data) {
    if (isLegacyFlatArray(data)) {
      return [{
        id: PRIMARY_LIST_ID,
        name: PRIMARY_LIST_NAME,
        primary: true,
        items: data.map(normalizeItem),
        sharing: false,
        shareToken: null,
      }];
    }

    if (!Array.isArray(data)) {
      return [{ id: PRIMARY_LIST_ID, name: PRIMARY_LIST_NAME, primary: true, items: [], sharing: false, shareToken: null }];
    }

    const normalized = data.map(list => ({
      id: String(list.id),
      name: String(list.name || 'Untitled list'),
      primary: !!list.primary,
      items: Array.isArray(list.items) ? list.items.map(normalizeItem) : [],
      // sharing is a UI-facing mirror of shareToken (true once a token
      // exists), kept as its own field only so the "turning on sharing..."
      // in-between state -- after clicking Share but before the save
      // round trip returns a real token -- has somewhere to live. Every
      // real save response resets it back to !!shareToken (see saveToMetafield).
      sharing: !!list.shareToken,
      shareToken: list.shareToken || null,
    }));

    // Defensive only -- the worker enforces "exactly one primary list with
    // id 'wishlist'" server-side, so this should never actually trigger
    // against real saved data. Guards against a corrupt localStorage blob.
    if (!normalized.some(list => list.primary && list.id === PRIMARY_LIST_ID)) {
      normalized.unshift({ id: PRIMARY_LIST_ID, name: PRIMARY_LIST_NAME, primary: true, items: [], sharing: false, shareToken: null });
    }

    return normalized;
  }

  // ─── LOAD ──────────────────────────────────────────────────────────────────
  function loadFromLocalStorage() {
    try {
      return normalizeLists(JSON.parse(localStorage.getItem(STORAGE_KEY)));
    } catch {
      return normalizeLists([]);
    }
  }

  function loadFromMetafield() {
    // Metafield value is rendered server-side by Liquid into a JSON script
    // tag (see snippets/wishlist-customer-data.liquid) so the page doesn't
    // need a round trip just to know what's saved.
    const el = document.getElementById('backbone-wishlist-data');
    if (el && el.textContent.trim()) {
      try {
        return normalizeLists(JSON.parse(el.textContent.trim()));
      } catch {
        return normalizeLists([]);
      }
    }
    return normalizeLists([]);
  }

  // ─── SAVE ──────────────────────────────────────────────────────────────────
  // Every item carries a variantId in memory (see normalizeItem) so
  // comparisons stay simple, but it's null on every item until variant-level
  // saves are built (issue #9) -- storing "variantId": null on every single
  // item is pure dead weight against Shopify's 128KB JSON metafield limit,
  // so it's dropped here, at the point data actually gets persisted.
  function serializeForStorage(listsToSerialize) {
    return listsToSerialize.map(list => ({
      id: list.id,
      name: list.name,
      primary: list.primary,
      items: list.items.map(item => (item.variantId ? { productId: item.productId, variantId: item.variantId } : { productId: item.productId })),
      // sharing is the worker's real signal for "mint/keep a token vs.
      // clear it" -- see parseWishlist in backbone-wishlist-worker. The
      // token itself is echoed back (when known) so a routine save
      // doesn't rotate a link that's already been shared out.
      sharing: !!list.sharing,
      ...(list.shareToken ? { shareToken: list.shareToken } : {}),
    }));
  }

  async function saveWishlist() {
    if (IS_LOGGED_IN) {
      await saveToMetafield();
    } else {
      saveToLocalStorage();
    }
    updateBadge();
  }

  function saveToLocalStorage() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(serializeForStorage(lists)));
  }

  async function saveToMetafield() {
    // Not the Storefront API: metafieldsSet for a customer-owned metafield
    // isn't available there at any version -- it's an Admin API mutation
    // only. This posts to the Backbone Wishlist app's App Proxy instead
    // (same-origin, no token needed here at all -- Shopify signs the
    // forwarded request and identifies the customer server-side via
    // logged_in_customer_id), which holds the real Admin API credentials
    // and performs the write. See backbone-wishlist-worker repo.
    try {
      const response = await fetch('/apps/wishlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wishlist: serializeForStorage(lists) }),
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        console.error('Backbone Wishlist: Save failed', result);
        showToast("Couldn't save your wishlist. Please try again.");
        return;
      }

      // The worker is the only place a newly minted share token exists --
      // adopt its response as the new canonical state so a "Share" click
      // picks up the real token (and `sharing` gets reconciled back to
      // !!shareToken, clearing the optimistic in-between state).
      if (result.wishlist) {
        lists = normalizeLists(result.wishlist);
      }
    } catch (err) {
      console.error('Backbone Wishlist: Network error', err);
      showToast("Couldn't save your wishlist. Please try again.");
    }
  }

  // ─── STOREFRONT API HELPER ─────────────────────────────────────────────────
  async function storefrontFetch(query, variables) {
    const response = await fetch(`https://${SHOP_DOMAIN}/api/${API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query, variables }),
    });
    return response.json();
  }

  function variantIdFromGid(gid) {
    return gid.replace('gid://shopify/ProductVariant/', '');
  }

  // ─── CART ──────────────────────────────────────────────────────────────────
  // Same integration the theme's own product-form uses: find whichever
  // cart UI is on the page and hand it the /cart/add.js response so it
  // re-renders itself (opens the notification/drawer, updates the header
  // bubble) exactly like adding from a product page does -- a wishlist add
  // shouldn't feel like a different, lesser path through the store.
  function getCartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  async function addVariantsToCart(items) {
    if (items.length === 0) return { success: false, error: 'Nothing to add' };

    const cart = getCartElement();
    const body = { items };
    if (cart) {
      body.sections = cart.getSectionsToRender().map(section => section.id);
      body.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const result = await response.json();

      if (!response.ok) {
        // Shopify can genuinely add *some* of a multi-item request and
        // still respond with a non-2xx status and an `errors` message
        // like "Only 1 item was added to your cart due to availability"
        // -- real-time inventory can shift between page load (when
        // availability was last checked) and the click. That's a partial
        // outcome, not a clean failure, and the items that did get added
        // are real -- but this response shape carries no `sections` data
        // to hand to cart.renderContents() the way a clean success does,
        // so the cart icon is refreshed separately instead.
        const message = typeof result.errors === 'string' ? result.errors : (result.description || result.message || "Couldn't add to cart");
        if (cart) await refreshCartIconBubble();
        return { success: false, partial: true, error: message };
      }

      if (cart) {
        // /cart/add.js only returns a top-level `key` when the *request*
        // body used the classic single {id, quantity} form -- the
        // {items: [...]} array form used here (uniformly, even for a
        // single item, to keep one code path for both single and bulk
        // adds) always comes back as {items: [...]} instead, with no
        // top-level key at all. cart-notification.js needs `cartItemKey`
        // to find which line to highlight in its popup and throws trying
        // to read a null selector match without it -- approximate with
        // the last item added, since Shopify processes the array in order.
        if (!result.key && Array.isArray(result.items) && result.items.length > 0) {
          result.key = result.items[result.items.length - 1].key;
        }
        cart.renderContents(result);
      }
      return { success: true };
    } catch (err) {
      console.error('Backbone Wishlist: Add to cart failed', err);
      return { success: false, error: "Couldn't add to cart" };
    }
  }

  // Used only for the partial-success case above -- Shopify's Section
  // Rendering API (a plain GET, distinct from the /cart/add.js response)
  // to at least keep the header's cart count honest when some items were
  // genuinely added but the response shape didn't carry cart.renderContents()
  // what it needs. Deliberately only touches the icon bubble, not the
  // notification popup itself -- there's no specific new line to
  // highlight in a partial/mixed-result add the way a clean one has.
  async function refreshCartIconBubble() {
    try {
      const response = await fetch(`${window.location.pathname}?sections=cart-icon-bubble`);
      const result = await response.json();
      const html = result['cart-icon-bubble'];
      if (!html) return;
      const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      const target = document.getElementById('cart-icon-bubble');
      if (target && section) target.innerHTML = section.innerHTML;
    } catch (err) {
      console.error('Backbone Wishlist: Failed to refresh cart icon', err);
    }
  }

  // ─── LIST / ITEM HELPERS ───────────────────────────────────────────────────
  function getPrimaryList() {
    return lists.find(list => list.primary) || lists[0];
  }

  function findList(listId) {
    return lists.find(list => list.id === listId);
  }

  function generateListId() {
    let id;
    do {
      id = Math.random().toString(36).slice(2, 8);
    } while (id === PRIMARY_LIST_ID || findList(id));
    return id;
  }

  function itemMatches(item, productId, variantId) {
    if (item.productId !== String(productId)) return false;
    if (variantId === undefined) return true;
    return item.variantId === variantId;
  }

  function isInList(listId, productId, variantId) {
    const list = findList(listId);
    if (!list) return false;
    return list.items.some(item => itemMatches(item, productId, variantId));
  }

  // Used by the heart everywhere (collection cards + product page quick-add)
  // -- hearts only ever reflect/target the primary list, per the confirmed
  // UX: one-click save goes to "Wishlist," picking a different list is a
  // deliberate action via the list picker on the product page. variantId
  // is null on a collection card (no variant context there) or a
  // single-variant product page, or a real id on a product page with an
  // actual picker -- see getSelectedVariantId.
  function isInWishlist(productId, variantId = null) {
    return isInList(PRIMARY_LIST_ID, productId, variantId);
  }

  // Every list (primary or custom) that currently holds this exact
  // product+variant -- used only for the product-page label text, so a
  // customer who saved something to a custom list only (not the primary)
  // isn't told nothing's saved. The heart itself still only reflects/
  // targets the primary list.
  function listsContaining(productId, variantId) {
    return lists.filter(list => list.items.some(item => itemMatches(item, productId, variantId)));
  }

  // ─── MUTATIONS (state only -- callers handle save + re-render) ────────────
  function toggleItem(productId, listId = PRIMARY_LIST_ID, variantId = null) {
    const list = findList(listId) || getPrimaryList();
    const index = list.items.findIndex(item => itemMatches(item, productId, variantId));

    if (index === -1) {
      list.items.push({ productId: String(productId), variantId });
      return { added: true, list };
    }
    list.items.splice(index, 1);
    return { added: false, list };
  }

  function addItemToList(productId, listId, variantId = null) {
    const list = findList(listId);
    if (!list) return false;
    if (isInList(listId, productId, variantId)) return false;
    list.items.push({ productId: String(productId), variantId });
    return true;
  }

  function removeItemFromList(productId, listId, variantId = null) {
    const list = findList(listId);
    if (!list) return false;
    const index = list.items.findIndex(item => itemMatches(item, productId, variantId));
    if (index === -1) return false;
    list.items.splice(index, 1);
    return true;
  }

  function moveItem(productId, fromListId, toListId, variantId = null) {
    const from = findList(fromListId);
    const to = findList(toListId);
    if (!from || !to || fromListId === toListId) return false;

    const index = from.items.findIndex(item => itemMatches(item, productId, variantId));
    if (index === -1) return false;

    const [item] = from.items.splice(index, 1);
    if (!to.items.some(existing => existing.productId === item.productId && existing.variantId === item.variantId)) {
      to.items.push(item);
    }
    return true;
  }

  function createList(name) {
    const trimmed = (name || '').trim();
    if (!trimmed) return null;
    const list = { id: generateListId(), name: trimmed, primary: false, items: [] };
    lists.push(list);
    return list;
  }

  function renameList(listId, name) {
    const list = findList(listId);
    const trimmed = (name || '').trim();
    if (!list || !trimmed) return false;
    list.name = trimmed;
    return true;
  }

  // Primary list can never be deleted -- checked here even though the UI
  // never renders a delete control for it, and the worker rejects it too,
  // since this function is the one place all three layers ultimately rely
  // on to actually not do it.
  function deleteList(listId, { moveItemsTo } = {}) {
    const list = findList(listId);
    if (!list || list.primary) return false;

    if (moveItemsTo && list.items.length) {
      const target = findList(moveItemsTo);
      if (target) {
        list.items.forEach(item => {
          if (!target.items.some(existing => existing.productId === item.productId && existing.variantId === item.variantId)) {
            target.items.push(item);
          }
        });
      }
    }

    lists = lists.filter(l => l.id !== listId);
    return true;
  }

  // Only sets local intent -- the real token only ever comes from the
  // worker's save response (see saveToMetafield), since it has to be
  // minted somewhere that can't be spoofed by the client.
  function setListSharing(listId, enabled) {
    const list = findList(listId);
    if (!list) return false;
    list.sharing = enabled;
    if (!enabled) list.shareToken = null;
    return true;
  }

  function buildShareUrl(list) {
    const url = new URL('/pages/wishlist', window.location.origin);
    url.searchParams.set('share_customer', CUSTOMER_ID);
    url.searchParams.set('share_list', list.id);
    url.searchParams.set('share_token', list.shareToken);
    return url.toString();
  }

  // ─── RENDER: HEARTS ─────────────────────────────────────────────────────────
  // The product page's own heart uses the live-tracked selection
  // (currentProductVariantId, kept in sync as the customer switches
  // options -- see bindVariantChangeListener). Everywhere else (collection
  // cards) there's no picker to track live, so it falls back to whatever
  // real variant wishlist-heart.liquid resolved and rendered server-side
  // (the product's default/first-available one) -- null only ever remains
  // for a genuinely single-variant product. This is what keeps a
  // multi-variant product from ever being saved as the ambiguous bare
  // "parent" item, on either surface.
  function getHeartVariantId(btn) {
    return btn.closest('.product-wishlist') ? currentProductVariantId : (btn.dataset.variantId || null);
  }

  function renderHearts() {
    document.querySelectorAll('.wishlist-heart').forEach(btn => {
      const id = btn.dataset.productId;
      const variantId = getHeartVariantId(btn);
      const active = isInWishlist(id, variantId);
      btn.classList.toggle('wishlist-heart--active', active);
      btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');

      const outlineIcon = btn.querySelector('.wishlist-heart__icon--outline');
      const filledIcon = btn.querySelector('.wishlist-heart__icon--filled');
      if (outlineIcon) outlineIcon.hidden = active;
      if (filledIcon) filledIcon.hidden = !active;

      const label = btn.parentElement?.querySelector('.product-wishlist__label');
      if (label) {
        const otherLists = listsContaining(id, variantId).filter(list => !list.primary);
        if (active && otherLists.length) {
          label.textContent = `Added to your wishlist (+ ${otherLists.map(list => list.name).join(', ')})`;
        } else if (active) {
          label.textContent = 'Added to your wishlist';
        } else if (otherLists.length) {
          label.textContent = `Saved to ${otherLists.map(list => list.name).join(', ')}`;
        } else {
          label.textContent = 'Save to Wishlist';
        }
      }
    });
  }

  function updateBadge() {
    const badge = document.querySelector('.wishlist-count-bubble');
    if (!badge) return;
    const count = getPrimaryList()?.items.length || 0;
    const countEl = badge.querySelector('.wishlist-count-bubble__count');
    if (countEl) countEl.textContent = count > 0 ? count : '';
    badge.hidden = count === 0;
  }

  // ─── RENDER: PRODUCT-PAGE LIST PICKER ──────────────────────────────────────
  function renderListPickers() {
    document.querySelectorAll('.wishlist-list-picker').forEach(picker => {
      const productId = picker.dataset.productId;
      const listEl = picker.querySelector('.wishlist-list-picker__lists');
      if (!listEl) return;

      listEl.innerHTML = lists.map(list => {
        // The picker only ever renders on the product page, so it always
        // has variant context (null on a single-variant product, same as
        // the heart there).
        const active = isInList(list.id, productId, currentProductVariantId);
        return `
          <li>
            <button
              type="button"
              class="wishlist-list-picker__item${active ? ' wishlist-list-picker__item--active' : ''}"
              data-list-id="${escapeHtml(list.id)}"
              data-product-id="${escapeHtml(productId)}"
              aria-pressed="${active}"
            >
              <span class="wishlist-list-picker__checkbox" aria-hidden="true"></span>
              ${escapeHtml(list.name)}
            </button>
          </li>
        `;
      }).join('');

      picker.hidden = false;
    });
  }

  function closeAllListPickers() {
    document.querySelectorAll('.wishlist-list-picker__menu').forEach(menu => { menu.hidden = true; });
    document.querySelectorAll('.wishlist-list-picker__trigger').forEach(trigger => {
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  function getWishlistPageContainer() {
    return document.getElementById('wishlist-products');
  }

  function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value ?? '';
    return div.innerHTML;
  }

  // ─── RENDER: WISHLIST PAGE ──────────────────────────────────────────────────
  async function renderWishlistPage() {
    const container = getWishlistPageContainer();
    if (!container) return;

    if (!findList(activeListId)) activeListId = PRIMARY_LIST_ID;
    const activeList = findList(activeListId);

    container.innerHTML = `
      <div class="wishlist-manager">
        ${renderNotifyPreferenceMarkup()}
        ${renderTabsMarkup()}
        ${pendingDeleteListId ? renderDeleteConfirmMarkup(findList(pendingDeleteListId)) : renderListHeaderMarkup(activeList)}
        <div class="wishlist-products-grid" id="wishlist-active-list-items"></div>
      </div>
    `;

    if (pendingDeleteListId) return; // grid stays empty behind the confirm panel

    const itemsContainer = container.querySelector('#wishlist-active-list-items');
    await renderListItems(activeList, itemsContainer);
  }

  // Global toggle, not per-list or per-item -- deliberately simple
  // ("notify me about my wishlist," not a control on every saved item).
  // Guests get nothing here: there's no Shopify customer id to resolve to
  // a Klaviyo profile for them, and the whole point is alerts reaching
  // them somewhere, which requires being a real logged-in customer.
  function renderNotifyPreferenceMarkup() {
    if (!IS_LOGGED_IN) return '';
    return `
      <label class="wishlist-notify-preference">
        <input type="checkbox" class="wishlist-notify-toggle" ${notifyPreferenceEnabled ? 'checked' : ''}>
        Notify me by email or text when a saved item is back in stock or drops in price
      </label>
    `;
  }

  function renderTabsMarkup() {
    const tabs = lists.map(list => `
      <button
        type="button"
        class="wishlist-tabs__tab${list.id === activeListId ? ' wishlist-tabs__tab--active' : ''}"
        data-list-id="${escapeHtml(list.id)}"
        role="tab"
        aria-selected="${list.id === activeListId}"
      >
        ${escapeHtml(list.name)}
        <span class="wishlist-tabs__count">${list.items.length}</span>
      </button>
    `).join('');

    return `
      <div class="wishlist-tabs" role="tablist">
        ${tabs}
        <button type="button" class="wishlist-tabs__new">+ New list</button>
      </div>
    `;
  }

  function renderListHeaderMarkup(list) {
    if (!list) return '';
    const shareEnableBtn = IS_LOGGED_IN && !list.sharing
      ? `<button type="button" class="wishlist-list-header__share-enable" data-list-id="${escapeHtml(list.id)}">Share</button>`
      : '';

    const addAllBtn = list.items.length > 0
      ? `<button type="button" class="wishlist-list-header__add-all">Add all to cart</button>`
      : '';

    return `
      <div class="wishlist-list-header">
        <h2 class="wishlist-list-header__name">${escapeHtml(list.name)}</h2>
        <div class="wishlist-list-header__actions">
          ${addAllBtn}
          <button type="button" class="wishlist-list-header__rename" data-list-id="${escapeHtml(list.id)}">Rename</button>
          ${shareEnableBtn}
          ${list.primary ? '' : `<button type="button" class="wishlist-list-header__delete" data-list-id="${escapeHtml(list.id)}">Delete list</button>`}
        </div>
      </div>
      ${IS_LOGGED_IN && list.sharing ? renderSharePanelMarkup(list) : ''}
      ${!IS_LOGGED_IN ? '<p class="wishlist-share-hint">Log in to share this list.</p>' : ''}
    `;
  }

  function renderSharePanelMarkup(list) {
    if (!list.shareToken) {
      return '<div class="wishlist-share-panel"><p>Turning on sharing…</p></div>';
    }

    const url = buildShareUrl(list);
    return `
      <div class="wishlist-share-panel">
        <p>Anyone with this link can view "${escapeHtml(list.name)}" -- no account details are shown, and they can't make changes.</p>
        <div class="wishlist-share-panel__row">
          <input type="text" class="wishlist-share-panel__link" value="${escapeHtml(url)}" readonly>
          <button type="button" class="wishlist-share-panel__copy" data-url="${escapeHtml(url)}">Copy link</button>
        </div>
        <button type="button" class="wishlist-share-panel__disable" data-list-id="${escapeHtml(list.id)}">Turn off sharing</button>
      </div>
    `;
  }

  function renderDeleteConfirmMarkup(list) {
    if (!list) return '';
    const otherLists = lists.filter(l => l.id !== list.id);
    const options = otherLists.map(l => `<option value="${escapeHtml(l.id)}">${escapeHtml(l.name)}</option>`).join('');

    return `
      <div class="wishlist-delete-confirm">
        <p>
          "<strong>${escapeHtml(list.name)}</strong>" still has ${list.items.length}
          item${list.items.length === 1 ? '' : 's'} saved. What would you like to do?
        </p>
        <div class="wishlist-delete-confirm__row">
          <select class="wishlist-delete-confirm__target">
            <option value="">Choose a list to move items to…</option>
            ${options}
          </select>
          <button type="button" class="wishlist-delete-confirm__move-delete" disabled>Move items &amp; delete list</button>
        </div>
        <div class="wishlist-delete-confirm__row">
          <button type="button" class="wishlist-delete-confirm__delete-anyway">Delete list without moving items</button>
          <button type="button" class="wishlist-delete-confirm__cancel">Cancel</button>
        </div>
      </div>
    `;
  }

  // Renders a single list's saved products by looking them up in bulk via
  // the Storefront API. readOnly drops the move/remove controls -- used
  // by the public shared-list view (see renderSharedListView), where the
  // visitor isn't the list's owner and shouldn't be able to change it.
  async function renderListItems(list, container, { readOnly = false } = {}) {
    if (!container) return;

    if (!list || list.items.length === 0) {
      container.innerHTML = emptyStateMarkup(list, readOnly);
      return;
    }

    if (!STOREFRONT_TOKEN || !SHOP_DOMAIN) {
      container.innerHTML = '<p class="wishlist-error">Unable to load wishlist right now. Please try again.</p>';
      return;
    }

    container.innerHTML = '<p class="wishlist-loading">Loading your wishlist...</p>';

    // Mixed Product/ProductVariant lookup in one request: an item saved
    // with a specific variant (post-#9) queries that ProductVariant
    // directly for its own title/price/image; a legacy item (variantId
    // null, saved before #9 or from a collection-card heart) queries the
    // Product instead and falls back to its default/first-available
    // variant, same as before #9. nodes() preserves input order, so each
    // result lines up with list.items by index -- no id round-tripping
    // needed to re-associate them.
    const ids = list.items.map(item => item.variantId
      ? `gid://shopify/ProductVariant/${item.variantId}`
      : `gid://shopify/Product/${item.productId}`
    );

    const query = `
      query getWishlistItems($ids: [ID!]!) {
        nodes(ids: $ids) {
          __typename
          ... on Product {
            title
            handle
            featuredImage {
              url
              altText
            }
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            selectedOrFirstAvailableVariant {
              id
              availableForSale
            }
          }
          ... on ProductVariant {
            id
            title
            availableForSale
            price {
              amount
              currencyCode
            }
            image {
              url
              altText
            }
            product {
              title
              handle
              featuredImage {
                url
                altText
              }
            }
          }
        }
      }
    `;

    try {
      const response = await storefrontFetch(query, { ids });
      const nodes = response.data?.nodes || [];

      // Pair by position (not id) since a variant node has no bare
      // productId of its own to match back against -- item.productId is
      // already known locally, no need to ask the API for it.
      const displayItems = list.items
        .map((item, index) => normalizeDisplayItem(item, nodes[index]))
        .filter(Boolean);

      if (displayItems.length === 0) {
        container.innerHTML = emptyStateMarkup(list, readOnly);
        activeListAddableItems = [];
        return;
      }

      const otherLists = readOnly ? [] : lists.filter(l => l.id !== list.id);

      // Stashed for the per-list "Add all to cart" bulk action -- it needs
      // to know which items currently have a purchasable variant without
      // re-fetching, and re-deriving it from `list.items` alone can't tell
      // sold-out apart from in-stock.
      activeListAddableItems = displayItems
        .filter(d => d.available)
        .map(d => ({ variantId: d.cartVariantId }));

      container.innerHTML = displayItems.map(d => {
        const moveOptions = otherLists.map(l => `<option value="${escapeHtml(l.id)}">${escapeHtml(l.name)}</option>`).join('');
        const variantAttr = d.savedVariantId ? ` data-variant-id="${escapeHtml(d.savedVariantId)}"` : '';

        return `
          <div class="wishlist-card" data-product-id="${escapeHtml(d.productId)}"${variantAttr}>
            <a href="/products/${d.handle}" class="wishlist-card__image-link">
              ${d.image
                ? `<img src="${d.image.url}" alt="${escapeHtml(d.image.altText || d.title)}" loading="lazy">`
                : '<div class="wishlist-card__image-placeholder"></div>'
              }
            </a>
            <div class="wishlist-card__info">
              <a href="/products/${d.handle}" class="wishlist-card__title">${escapeHtml(d.title)}</a>
              <p class="wishlist-card__price">$${d.price} ${d.currency}</p>
            </div>
            <div class="wishlist-card__actions">
              ${d.cartVariantId ? `
                <button class="wishlist-card__add-to-cart" data-variant-id="${escapeHtml(d.cartVariantId)}" ${d.available ? '' : 'disabled'}>
                  ${d.available ? 'Add to Cart' : 'Sold out'}
                </button>
              ` : ''}
              ${readOnly ? '' : `
                ${otherLists.length ? `
                  <select class="wishlist-card__move-to" data-product-id="${escapeHtml(d.productId)}" data-list-id="${escapeHtml(list.id)}"${variantAttr}>
                    <option value="">Move to…</option>
                    ${moveOptions}
                  </select>
                ` : ''}
                <button class="wishlist-card__remove" data-product-id="${escapeHtml(d.productId)}" data-list-id="${escapeHtml(list.id)}"${variantAttr} aria-label="Remove from ${escapeHtml(list.name)}">
                  Remove
                </button>
              `}
            </div>
          </div>
        `;
      }).join('');

    } catch (err) {
      console.error('Backbone Wishlist: Failed to load products', err);
      container.innerHTML = '<p class="wishlist-error">Unable to load wishlist right now. Please try again.</p>';
    }
  }

  // Reduces either API shape (a specific ProductVariant, or a Product
  // for legacy/variant-less items) down to one common shape the card
  // renderer doesn't need to know the difference for. Returns null for a
  // node Shopify couldn't resolve at all (product or variant deleted).
  function normalizeDisplayItem(item, node) {
    if (!node) return null;

    if (node.__typename === 'ProductVariant') {
      const variantId = variantIdFromGid(node.id);
      const image = node.image || node.product.featuredImage;
      return {
        productId: item.productId,
        savedVariantId: variantId,
        cartVariantId: variantId,
        handle: node.product.handle,
        title: `${node.product.title} — ${node.title}`,
        price: parseFloat(node.price.amount).toFixed(2),
        currency: node.price.currencyCode,
        image,
        available: !!node.availableForSale,
      };
    }

    // Legacy / variant-less item -- product-level display, falls back to
    // the default/first-available variant for add-to-cart, same as
    // pre-#9 behavior.
    const fallbackVariant = node.selectedOrFirstAvailableVariant;
    return {
      productId: item.productId,
      savedVariantId: null,
      cartVariantId: fallbackVariant ? variantIdFromGid(fallbackVariant.id) : null,
      handle: node.handle,
      title: node.title,
      price: parseFloat(node.priceRange.minVariantPrice.amount).toFixed(2),
      currency: node.priceRange.minVariantPrice.currencyCode,
      image: node.featuredImage,
      available: !!fallbackVariant?.availableForSale,
    };
  }

  function emptyStateMarkup(list, readOnly = false) {
    const label = readOnly
      ? `"${escapeHtml(list?.name || 'This list')}" doesn't have anything saved yet.`
      : (list && !list.primary ? `"${escapeHtml(list.name)}" is empty.` : 'Nothing saved yet. Start browsing.');
    return `
      <div class="wishlist-empty">
        <p>${label}</p>
        <a href="${ALL_PRODUCTS_URL}" class="button button--primary">Shop All</a>
      </div>
    `;
  }

  // ─── SHARED (PUBLIC, READ-ONLY) LIST VIEW ──────────────────────────────────
  function getShareParamsFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const customer = params.get('share_customer');
    const list = params.get('share_list');
    const token = params.get('share_token');
    if (!customer || !list || !token) return null;
    return { customer, list, token };
  }

  async function renderSharedListView({ customer, list, token }) {
    const container = getWishlistPageContainer();
    if (!container) return;

    // The normal page's static Liquid subtitle ("Saved items sync across
    // your devices" / "Log in to save...") doesn't apply to a visitor
    // looking at someone else's shared list -- nothing server-side knows
    // this is a share link (see main-page-wishlist.liquid), so it's
    // swapped out here instead.
    const subtitle = document.querySelector('.wishlist-page__subtitle');
    if (subtitle) subtitle.textContent = "You're viewing a shared wishlist.";

    container.innerHTML = '<p class="wishlist-loading">Loading shared list...</p>';

    try {
      const params = new URLSearchParams({ customer, list, token });
      const response = await fetch(`/apps/wishlist/shared?${params.toString()}`);
      const result = await response.json();

      if (!response.ok || !result.success) {
        container.innerHTML = '<p class="wishlist-error">This shared list is no longer available.</p>';
        return;
      }

      const heading = document.createElement('h2');
      heading.className = 'wishlist-list-header__name';
      heading.textContent = result.list.name;
      container.innerHTML = '';
      container.appendChild(heading);

      const grid = document.createElement('div');
      grid.className = 'wishlist-products-grid';
      container.appendChild(grid);

      await renderListItems(result.list, grid, { readOnly: true });
    } catch (err) {
      console.error('Backbone Wishlist: Failed to load shared list', err);
      container.innerHTML = '<p class="wishlist-error">This shared list is no longer available.</p>';
    }
  }

  // ─── TOAST ─────────────────────────────────────────────────────────────────
  function showToast(message) {
    let toast = document.getElementById('wishlist-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'wishlist-toast';
      toast.className = 'wishlist-toast';
      document.body.appendChild(toast);
    }
    toast.innerHTML = message;
    toast.classList.add('wishlist-toast--visible');
    clearTimeout(toast._timeout);
    toast._timeout = setTimeout(() => {
      toast.classList.remove('wishlist-toast--visible');
    }, 3500);
  }

  // ─── EVENTS ────────────────────────────────────────────────────────────────
  function bindEvents() {
    document.addEventListener('click', async e => {
      const heart = e.target.closest('.wishlist-heart');
      if (heart) {
        e.preventDefault();
        const { added } = toggleItem(heart.dataset.productId, PRIMARY_LIST_ID, getHeartVariantId(heart));
        showToast(
          added
            ? (IS_LOGGED_IN ? 'Added to your wishlist.' : `Added to wishlist. <a href="${LOGIN_URL}">Log in</a> to save across devices.`)
            : 'Removed from your wishlist.'
        );
        renderHearts();
        renderListPickers();
        await saveWishlist();
        if (getWishlistPageContainer()) await renderWishlistPage();
        return;
      }

      const pickerTrigger = e.target.closest('.wishlist-list-picker__trigger');
      if (pickerTrigger) {
        e.preventDefault();
        const menu = pickerTrigger.parentElement.querySelector('.wishlist-list-picker__menu');
        const willOpen = menu.hidden;
        closeAllListPickers();
        if (willOpen) {
          menu.hidden = false;
          pickerTrigger.setAttribute('aria-expanded', 'true');
        }
        return;
      }

      const pickerItem = e.target.closest('.wishlist-list-picker__item');
      if (pickerItem) {
        e.preventDefault();
        const { productId, listId } = pickerItem.dataset;
        if (isInList(listId, productId, currentProductVariantId)) {
          removeItemFromList(productId, listId, currentProductVariantId);
        } else {
          addItemToList(productId, listId, currentProductVariantId);
        }
        renderHearts();
        renderListPickers();
        await saveWishlist();
        if (getWishlistPageContainer()) await renderWishlistPage();
        return;
      }

      const newListFromPicker = e.target.closest('.wishlist-list-picker__new-list');
      if (newListFromPicker) {
        e.preventDefault();
        const picker = newListFromPicker.closest('.wishlist-list-picker');
        const name = window.prompt('Name your new list:');
        const list = createList(name);
        if (list) {
          addItemToList(picker.dataset.productId, list.id, currentProductVariantId);
          renderHearts();
          renderListPickers();
          await saveWishlist();
          if (getWishlistPageContainer()) await renderWishlistPage();
        }
        return;
      }

      if (!e.target.closest('.wishlist-list-picker')) {
        closeAllListPickers();
      }

      const tab = e.target.closest('.wishlist-tabs__tab');
      if (tab) {
        activeListId = tab.dataset.listId;
        pendingDeleteListId = null;
        await renderWishlistPage();
        return;
      }

      const newListTab = e.target.closest('.wishlist-tabs__new');
      if (newListTab) {
        const name = window.prompt('Name your new list:');
        const list = createList(name);
        if (list) {
          activeListId = list.id;
          renderListPickers();
          await saveWishlist();
          await renderWishlistPage();
        }
        return;
      }

      const renameBtn = e.target.closest('.wishlist-list-header__rename');
      if (renameBtn) {
        const list = findList(renameBtn.dataset.listId);
        const name = window.prompt('Rename this list:', list?.name || '');
        if (renameList(renameBtn.dataset.listId, name)) {
          renderListPickers();
          await saveWishlist();
          await renderWishlistPage();
        }
        return;
      }

      const deleteBtn = e.target.closest('.wishlist-list-header__delete');
      if (deleteBtn) {
        const list = findList(deleteBtn.dataset.listId);
        if (!list) return;
        if (list.items.length === 0) {
          deleteList(list.id);
          activeListId = PRIMARY_LIST_ID;
          renderListPickers();
          await saveWishlist();
          await renderWishlistPage();
        } else {
          pendingDeleteListId = list.id;
          await renderWishlistPage();
        }
        return;
      }

      const shareEnableBtn = e.target.closest('.wishlist-list-header__share-enable');
      if (shareEnableBtn) {
        setListSharing(shareEnableBtn.dataset.listId, true);
        await renderWishlistPage(); // shows "Turning on sharing..." immediately
        await saveWishlist(); // resolves to the real token via the server response
        await renderWishlistPage();
        return;
      }

      const shareDisableBtn = e.target.closest('.wishlist-share-panel__disable');
      if (shareDisableBtn) {
        setListSharing(shareDisableBtn.dataset.listId, false);
        await saveWishlist();
        await renderWishlistPage();
        return;
      }

      const shareCopyBtn = e.target.closest('.wishlist-share-panel__copy');
      if (shareCopyBtn) {
        try {
          await navigator.clipboard.writeText(shareCopyBtn.dataset.url);
          showToast('Link copied.');
        } catch {
          showToast("Couldn't copy the link -- select and copy it manually.");
        }
        return;
      }

      const cancelDelete = e.target.closest('.wishlist-delete-confirm__cancel');
      if (cancelDelete) {
        pendingDeleteListId = null;
        await renderWishlistPage();
        return;
      }

      const deleteAnyway = e.target.closest('.wishlist-delete-confirm__delete-anyway');
      if (deleteAnyway) {
        deleteList(pendingDeleteListId);
        activeListId = PRIMARY_LIST_ID;
        pendingDeleteListId = null;
        renderListPickers();
        await saveWishlist();
        await renderWishlistPage();
        return;
      }

      const moveDelete = e.target.closest('.wishlist-delete-confirm__move-delete');
      if (moveDelete) {
        const select = moveDelete.parentElement.querySelector('.wishlist-delete-confirm__target');
        if (!select.value) return;
        deleteList(pendingDeleteListId, { moveItemsTo: select.value });
        activeListId = select.value;
        pendingDeleteListId = null;
        renderListPickers();
        await saveWishlist();
        await renderWishlistPage();
        return;
      }

      const removeBtn = e.target.closest('.wishlist-card__remove');
      if (removeBtn) {
        removeItemFromList(removeBtn.dataset.productId, removeBtn.dataset.listId, removeBtn.dataset.variantId || null);
        renderHearts();
        renderListPickers();
        await saveWishlist();
        await renderWishlistPage();
        return;
      }

      const addToCartBtn = e.target.closest('.wishlist-card__add-to-cart');
      if (addToCartBtn) {
        if (addToCartBtn.disabled) return;
        const originalText = addToCartBtn.textContent;
        addToCartBtn.disabled = true;
        addToCartBtn.textContent = 'Adding…';

        const result = await addVariantsToCart([{ id: addToCartBtn.dataset.variantId, quantity: 1 }]);
        showToast(result.success ? 'Added to cart.' : (result.error || "Couldn't add to cart."));

        addToCartBtn.disabled = false;
        addToCartBtn.textContent = originalText;
        return;
      }

      const addAllBtn = e.target.closest('.wishlist-list-header__add-all');
      if (addAllBtn) {
        if (addAllBtn.disabled) return;

        // activeListAddableItems is set by the most recent renderListItems
        // call for whatever list is on screen -- items without a
        // purchasable variant were already excluded there, so the count
        // difference against the list's own item count is exactly how many
        // are being skipped as sold out.
        const items = activeListAddableItems.map(item => ({ id: item.variantId, quantity: 1 }));
        const skipped = (findList(activeListId)?.items.length || 0) - items.length;

        if (items.length === 0) {
          showToast('Nothing available to add right now.');
          return;
        }

        const originalText = addAllBtn.textContent;
        addAllBtn.disabled = true;
        addAllBtn.textContent = 'Adding…';

        const result = await addVariantsToCart(items);
        if (result.success) {
          const addedLabel = `Added ${items.length} item${items.length === 1 ? '' : 's'} to cart`;
          showToast(skipped > 0 ? `${addedLabel} -- ${skipped} sold out and ${skipped === 1 ? 'was' : 'were'} skipped.` : `${addedLabel}.`);
        } else {
          showToast(result.error || "Couldn't add to cart.");
        }

        addAllBtn.disabled = false;
        addAllBtn.textContent = originalText;
        return;
      }
    });

    document.addEventListener('change', async e => {
      const target = e.target.closest('.wishlist-delete-confirm__target');
      if (target) {
        const moveDeleteBtn = target.parentElement.querySelector('.wishlist-delete-confirm__move-delete');
        moveDeleteBtn.disabled = !target.value;
        return;
      }

      const moveTo = e.target.closest('.wishlist-card__move-to');
      if (moveTo && moveTo.value) {
        const moved = moveItem(moveTo.dataset.productId, moveTo.dataset.listId, moveTo.value, moveTo.dataset.variantId || null);
        if (moved) {
          showToast('Moved to your other list.');
          renderHearts();
          renderListPickers();
          await saveWishlist();
          await renderWishlistPage();
        }
        return;
      }

      const notifyToggle = e.target.closest('.wishlist-notify-toggle');
      if (notifyToggle) {
        const enabled = notifyToggle.checked;
        notifyToggle.disabled = true;
        try {
          const response = await fetch('/apps/wishlist/notify-preference', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ enabled }),
          });
          const result = await response.json();
          if (result.success) {
            notifyPreferenceEnabled = result.enabled;
            showToast(enabled ? 'Restock and price-drop alerts turned on.' : 'Restock and price-drop alerts turned off.');
          } else {
            notifyToggle.checked = !enabled;
            showToast("Couldn't update your preference. Please try again.");
          }
        } catch (err) {
          console.error('Backbone Wishlist: Failed to update notify preference', err);
          notifyToggle.checked = !enabled;
          showToast("Couldn't update your preference. Please try again.");
        }
        notifyToggle.disabled = false;
        return;
      }
    });
  }

  // ─── VARIANT TRACKING (product page only) ──────────────────────────────────
  // Reads whichever variant is currently selected via the theme's own
  // product form -- input[name="id"] is what product-form.js actually
  // submits to /cart/add, so it's the single source of truth for "what's
  // selected right now," already kept correct by the theme itself. Only
  // trusted when a real picker exists: a single-variant product has no
  // picker and nothing to choose, so it's left null and treated exactly
  // like pre-#9 data (product-level, no specific variant recorded).
  function getSelectedVariantId() {
    if (!document.querySelector('variant-selects, variant-radios')) return null;
    const input = document.querySelector('product-form form [name="id"]') || document.querySelector('form[action*="/cart/add"] [name="id"]');
    return input?.value ? String(input.value) : null;
  }

  // product-info.js publishes this (see assets/product-info.js) only once
  // a full variant selection has actually resolved -- not on every
  // incomplete option click -- with the resolved variant object already
  // attached, so no extra DOM read or Storefront round trip is needed here.
  function bindVariantChangeListener() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      currentProductVariantId = data?.variant?.id ? String(data.variant.id) : null;
      renderHearts();
      renderListPickers();
    });
  }

  // ─── GUEST MERGE ON LOGIN ──────────────────────────────────────────────────
  // Folds any lists saved while browsing as a guest into the customer's
  // account. Runs automatically on init() for logged-in customers (so it
  // fires on the first page load after login).
  //
  // Merge rule: the guest's primary list folds into the customer's primary
  // list. A guest's custom list merges into an existing customer list of
  // the same name (case-insensitive) if one exists, otherwise it's created
  // as a new list. Items are de-duped by product + variant.
  async function mergeGuestWishlist() {
    const guestLists = loadFromLocalStorage();
    const hasGuestItems = guestLists.some(list => list.items.length > 0);
    if (!hasGuestItems) return;

    guestLists.forEach(guestList => {
      let target;
      if (guestList.primary) {
        target = getPrimaryList();
      } else {
        target = lists.find(l => !l.primary && l.name.trim().toLowerCase() === guestList.name.trim().toLowerCase());
        if (!target) {
          target = { id: generateListId(), name: guestList.name, primary: false, items: [] };
          lists.push(target);
        }
      }
      guestList.items.forEach(item => {
        if (!target.items.some(existing => existing.productId === item.productId && existing.variantId === item.variantId)) {
          target.items.push(item);
        }
      });
    });

    await saveToMetafield();
    localStorage.removeItem(STORAGE_KEY);
  }

  return { init, isInWishlist, mergeGuestWishlist, renderWishlistPage };

})();

document.addEventListener('DOMContentLoaded', () => BackboneWishlist.init());
